import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './DashboardView.scss'

import { embedDashboard, EmbeddedDashboard, Size } from '@superset-ui/embedded-sdk'
import { type Dashboard } from '../../shared/insights'
import { Col, Container, Row } from 'react-bootstrap'

type SupersetQueryParams = {
  userId?: string
  accountId?: string
}

const DashboardView: React.FC<{
  dashboard: Dashboard
  initialHeight?: number
  urlParams?: SupersetQueryParams
}> = ({ dashboard: { id: dashboardId, domain, token, urlParams: dashboardUrlParams }, initialHeight, urlParams }) => {
  const getToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      resolve(token)
    })
  }, [token])

  const { userId, accountId } = urlParams ?? {}
  const mergedUrlParams = useMemo(() => ({ userId, accountId, ...dashboardUrlParams }), [userId, accountId, dashboardUrlParams])

  const [embeddedDashboard, setEmbeddedDashboard] = useState<EmbeddedDashboard>()
  const [embeddedDashboardSize, setEmbeddedDashboardSize] = useState<Size>()
  const dashboardRef = useRef<HTMLDivElement>(null)
  const embeddedDashboardRef = useRef<EmbeddedDashboard | undefined>()

  const embedAnalyticsDashboard = useCallback(async () => {
    if (dashboardRef.current) {
      const dashboard = await embedDashboard({
        id: dashboardId,
        supersetDomain: domain,
        mountPoint: dashboardRef.current,
        fetchGuestToken: () => getToken(),
        iframeSandboxExtras: ['allow-top-navigation'],
        dashboardUiConfig: {
          hideTitle: true,
          hideChartControls: false,
          hideTab: true,
          urlParams: mergedUrlParams,
          filters: {
            visible: true,
            expanded: false,
          },
        },
      })

      embeddedDashboardRef.current = dashboard
      setEmbeddedDashboard(dashboard)
    }
  }, [dashboardId, domain, getToken, mergedUrlParams])

  useEffect(() => {
    embedAnalyticsDashboard().catch((error) => console.error(error))
    return () => {
      embeddedDashboardRef.current?.unmount()
    }
  }, [embedAnalyticsDashboard])

  useEffect(() => {
    const interval = setInterval(async () => {
      const size = await embeddedDashboard?.getScrollSize()
      setEmbeddedDashboardSize(size)
    }, 2000)
    return () => {
      clearInterval(interval)
    }
  }, [embeddedDashboard])

  useEffect(() => {
    const dashboardElement = dashboardRef.current?.querySelector<HTMLIFrameElement>(`iframe`)
    if (embeddedDashboardSize && dashboardElement) {
      const dashboardHeight = embeddedDashboardSize.height
      dashboardElement.style.height = `${dashboardHeight.toString()}px`
    }
  }, [embeddedDashboardSize, dashboardId, initialHeight])

  return (
    <Container fluid className='mt-4'>
      <Row>
        <Col>
          <div ref={dashboardRef} className={`dashboard-container dashboard-container-${dashboardId}`}></div>
        </Col>
      </Row>
    </Container>
  )
}

export default DashboardView
